import { TFunction } from 'i18next'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerImageInfo } from '@/api/type-server'
import CodeIcon from '@/assets/icons/code.svg?react'
import PauseIcon from '@/assets/icons/pause.svg?react'
import ResetIcon from '@/assets/icons/reset.svg?react'
import ResumeIcon from '@/assets/icons/resume.svg?react'
import TransferIcon from '@/assets/icons/transfer.svg?react'
import { ImageProvider, ImageStoreStatus } from '@/consts/image'
import { formatBytes } from '@/utils/formatter'

import { UploadTask } from '../types'

type TaskPanelTab = 'upload' | 'import'

interface UploadStatusIndicatorProps {
  uploadTasks: UploadTask[]
  imageList: ServerImageInfo[]
  onContinueUpload?: (taskId: string, imageId: number) => void
  onRetryUpload?: (taskId: string) => void
  onPauseUpload?: (taskId: string) => void
  onResumeUpload?: (taskId: string) => void
}

function getErrCodeMessage(t: TFunction, errCode: number): string {
  return t(`common.errorCodes.${errCode}`, { defaultValue: String(errCode) })
}

function getImportItemDisplay(t: TFunction, image: ServerImageInfo) {
  switch (image.storeStatus) {
    case ImageStoreStatus.Pending:
    case ImageStoreStatus.Uploaded:
      return { label: t('customImage.status.importing'), color: 'text-tertiary' }
    case ImageStoreStatus.Formatted:
    case ImageStoreStatus.Finished:
      return {
        label: t('customImage.status.doneWithSize', {
          size: formatBytes(image.size) || '0 B',
        }),
        color: 'text-tertiary',
      }
    case ImageStoreStatus.Error:
      return {
        label: image.errCode
          ? t('customImage.status.errorWithCode', { message: getErrCodeMessage(t, image.errCode) })
          : t('customImage.status.error'),
        color: 'text-error',
      }
    default:
      return null
  }
}

export function UploadStatusIndicator({
  uploadTasks,
  imageList,
  onContinueUpload,
  onRetryUpload,
  onPauseUpload,
  onResumeUpload,
}: UploadStatusIndicatorProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TaskPanelTab>('upload')

  const importItems = useMemo(
    () =>
      imageList.filter(
        (image) =>
          image.provider === ImageProvider.RegistryURL && getImportItemDisplay(t, image) !== null,
      ),
    [imageList, t],
  )

  const badgeCount = uploadTasks.length + importItems.length

  const getUploadStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-500'
      case 'error':
        return 'text-error'
      default:
        return 'text-tertiary'
    }
  }

  const getUploadStatusIcon = (status: string, task?: UploadTask) => {
    switch (status) {
      case 'starting':
        return <PauseIcon className="w-4 h-4 text-secondary cursor-pointer" />
      case 'uploading':
        return (
          <PauseIcon
            className="w-4 h-4 text-secondary cursor-pointer"
            onClick={() => {
              if (task && onPauseUpload) {
                onPauseUpload(task.id)
              }
            }}
          />
        )
      case 'pausing':
        return <PauseIcon className="w-4 h-4 text-secondary cursor-not-allowed opacity-50" />
      case 'paused':
        return (
          <ResumeIcon
            className="w-4 h-4 text-secondary cursor-pointer"
            onClick={() => {
              if (task && onResumeUpload) {
                onResumeUpload(task.id)
              }
            }}
          />
        )
      case 'pending':
        return (
          <ResumeIcon
            className="w-4 h-4 text-secondary cursor-pointer"
            onClick={() => {
              if (task && task.imageId && onContinueUpload) {
                onContinueUpload(task.id, task.imageId)
              }
            }}
          />
        )
      case 'error':
        return (
          <ResetIcon
            className="w-4 h-4 text-secondary cursor-pointer"
            onClick={() => {
              if (task) {
                if (task.imageId && onContinueUpload) {
                  onContinueUpload(task.id, task.imageId)
                } else if (onRetryUpload) {
                  onRetryUpload(task.id)
                }
              }
            }}
          />
        )
      default:
        return null
    }
  }

  const renderTabButton = (tab: TaskPanelTab, count: number) => {
    const isActive = activeTab === tab
    return (
      <button
        type="button"
        className={`flex gap-1 items-center p-2 rounded-lg cursor-pointer border-0 bg-transparent ${
          isActive ? '' : 'opacity-60'
        }`}
        onClick={() => setActiveTab(tab)}
      >
        <span className={`t-x-sb ${isActive ? 'text-primary' : 'text-tertiary'}`}>
          {t(`customImage.taskPanel.${tab}`)}
        </span>
        <span
          className={`rounded-full w-5 h-5 flex items-center justify-center t-x-sb ${
            isActive ? 'bg-highlight/10 text-primary' : 'bg-neutral-50 text-tertiary'
          }`}
        >
          {count}
        </span>
      </button>
    )
  }

  return (
    <div className="relative group">
      <div className="bg-neutral-50 p-2 rounded-lg relative">
        <TransferIcon className="w-5 h-5" />
        {badgeCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-error text-white rounded-full min-w-4 h-4 px-1 flex items-center justify-center t-x-sb">
            {badgeCount > 99 ? '99+' : badgeCount}
          </div>
        )}
      </div>

      <div className="absolute left-0 top-full hidden group-hover:block pt-2 z-50">
        <div className="bg-panel-white shadow-card-100 rounded-xl py-4 min-w-[320px]">
          <div className="flex gap-2 items-center px-4 pb-2">
            {renderTabButton('upload', uploadTasks.length)}
            {renderTabButton('import', importItems.length)}
          </div>

          {activeTab === 'upload' ? (
            uploadTasks.length === 0 ? (
              <div className="t-xs text-tertiary px-4 py-2">
                {t('customImage.taskPanel.noUploadTasks')}
              </div>
            ) : (
              <div>
                {uploadTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-center justify-between py-3 px-4 hover:bg-panel-gray-100 ${
                      task.status === 'completed' ? 'bg-panel-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="bg-success/10 rounded-md p-1.5 flex-shrink-0">
                        <CodeIcon className="w-5 h-5 text-success" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="t-x-sb truncate" title={task.name}>
                          {task.name.toLowerCase()}
                        </div>
                        <div className={`t-xs ${getUploadStatusColor(task.status)}`}>
                          {task.status === 'starting' && t('customImage.status.starting')}
                          {task.status === 'uploading' &&
                            t('customImage.status.uploading', { progress: task.progress })}
                          {task.status === 'pausing' &&
                            t('customImage.status.pausing', { progress: task.progress })}
                          {task.status === 'paused' &&
                            t('customImage.status.paused', { progress: task.progress })}
                          {task.status === 'pending' && t('customImage.status.uploadRequired')}
                          {task.status === 'completed' &&
                            t('customImage.status.doneWithSize', {
                              size: task.originalFile ? formatBytes(task.originalFile.size) : '0 B',
                            })}
                          {task.status === 'error' && t('customImage.status.error')}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      {getUploadStatusIcon(task.status, task)}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : importItems.length === 0 ? (
            <div className="t-xs text-tertiary px-4 py-2">
              {t('customImage.taskPanel.noImportTasks')}
            </div>
          ) : (
            <div>
              {importItems.map((image) => {
                const display = getImportItemDisplay(t, image)!
                return (
                  <div
                    key={image.id}
                    className="flex items-center justify-between py-3 px-4 hover:bg-panel-gray-100"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="bg-success/10 rounded-md p-1.5 flex-shrink-0">
                        <CodeIcon className="w-5 h-5 text-success" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="t-x-sb truncate" title={image.alias}>
                          {(image.alias || image.name || '').toLowerCase()}
                        </div>
                        <div className={`t-xs ${display.color}`}>{display.label}</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
